import { useState, useEffect, useRef } from 'react';

const PICKS = [
  {
    name: "Camp Invention",
    description: "Hands-on STEM that actually keeps their attention all day.",
    buttonLabel: "Register Now",
    link: "https://invent.org/camp",
    badge: null,
    image: "https://public.readdy.ai/ai/img_res/97c329a0a7d54600347ff3364ce30ca5.jpg",
    tagColor: "bg-violet-50 border-violet-200 text-violet-700",
    tag: "STEM",
  },
  {
    name: "i9 Sports",
    description: "Flexible, low-pressure sports at Gary Pirkle Park — soccer, flag football, and girls-only flag football.",
    buttonLabel: "Register Now",
    link: "https://www.i9sports.com/programs/sugar-hill-buford-gary-pirkle-park-soccer-league-may-2026/176521",
    badge: null,
    image: "https://readdy.ai/api/search-image?query=young%20children%20playing%20soccer%20on%20a%20sunny%20green%20grass%20park%20field%2C%20kids%20in%20colorful%20jerseys%20running%20and%20laughing%2C%20bright%20blue%20sky%20background%2C%20joyful%20outdoor%20youth%20sports%20activity%2C%20warm%20sunny%20afternoon&width=600&height=400&seq=picks-sports-2&orientation=landscape",
    tagColor: "bg-orange-50 border-orange-200 text-orange-700",
    tag: "Sports",
  },
  {
    name: "Sugarloaf Performing Arts",
    description: "Great for kids who love to move, perform, or build confidence.",
    buttonLabel: "Register Now",
    link: "https://sugarloafperformingarts.org",
    badge: null,
    image: "https://readdy.ai/api/search-image?query=children%20performing%20on%20a%20bright%20stage%20in%20a%20performing%20arts%20recital%2C%20colorful%20costumes%2C%20spotlights%2C%20kids%20dancing%20and%20singing%20with%20big%20smiles%2C%20warm%20theater%20atmosphere%20with%20curtains&width=600&height=400&seq=picks-arts-3&orientation=landscape",
    tagColor: "bg-pink-50 border-pink-200 text-pink-700",
    tag: "Arts",
  },
  {
    name: "Elevate Summer Camp (Sugar Hill)",
    description: "High-energy days with structured activities, ninja course, and field trips.",
    buttonLabel: "Register Now",
    link: "https://sugarhillsummercamp.com",
    badge: "Mom's #1 Choice",
    image: "https://readdy.ai/api/search-image?query=energetic%20children%20on%20a%20ninja%20obstacle%20course%20at%20summer%20camp%2C%20kids%20climbing%20walls%20and%20swinging%20on%20ropes%20with%20huge%20smiles%2C%20bright%20outdoor%20gym%20setting%20with%20colorful%20equipment%2C%20high%20energy%20fun%20activity&width=600&height=400&seq=picks-ninja-4&orientation=landscape",
    tagColor: "bg-amber-50 border-amber-200 text-amber-700",
    tag: "Multi-Activity",
  },
  {
    name: "Camp Southern Ground",
    description: "A full camp experience focused on growth and independence.",
    buttonLabel: "Learn More",
    link: "https://campsouthernground.org",
    badge: null,
    image: "https://readdy.ai/api/search-image?query=children%20at%20a%20beautiful%20nature%20summer%20camp%20in%20the%20woods%2C%20kids%20hiking%20on%20a%20forest%20trail%20with%20counselors%2C%20lush%20green%20trees%20and%20sunlight%20filtering%20through%20leaves%2C%20team%20building%20outdoor%20adventure&width=600&height=400&seq=picks-nature-5&orientation=landscape",
    tagColor: "bg-emerald-50 border-emerald-200 text-emerald-700",
    tag: "Full Camp",
  },
];

const VISIBLE = 3;
const N = PICKS.length;
// Clones: last VISIBLE at front, all real items, first VISIBLE at end
const extended = [...PICKS.slice(-VISIBLE), ...PICKS, ...PICKS.slice(0, VISIBLE)];
const TOTAL_EXT = extended.length;

export default function CheyennesTopPicks() {
  // idx points into extended[]. Real items start at VISIBLE (=3).
  const [idx, setIdx] = useState(VISIBLE);
  const [animated, setAnimated] = useState(true);
  const pausedRef = useRef(false);

  const next = () => { setAnimated(true); setIdx(p => p + 1); };
  const prev = () => { setAnimated(true); setIdx(p => p - 1); };
  const goTo = (i: number) => { setAnimated(true); setIdx(VISIBLE + i); };

  // Infinite loop: after sliding into clone zone, silently snap back
  useEffect(() => {
    if (idx === VISIBLE + N) {
      const t = setTimeout(() => {
        setAnimated(false);
        setIdx(VISIBLE);
      }, 510);
      return () => clearTimeout(t);
    }
    if (idx === VISIBLE - 1) {
      const t = setTimeout(() => {
        setAnimated(false);
        setIdx(VISIBLE + N - 1);
      }, 510);
      return () => clearTimeout(t);
    }
  }, [idx]);

  // Re-enable animation after a no-animation snap
  useEffect(() => {
    if (!animated) {
      let raf1: number;
      let raf2: number;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setAnimated(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
  }, [animated]);

  // Auto-rotate every 4 s, pause on hover
  useEffect(() => {
    const interval = setInterval(() => {
      if (!pausedRef.current) {
        setAnimated(true);
        setIdx(p => p + 1);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Real index (0–N-1) for dot indicator
  const realIdx = ((idx - VISIBLE) % N + N) % N;

  // Track shift: each card occupies (1/VISIBLE) of container width
  // track total width = (TOTAL_EXT / VISIBLE) * 100% of container
  // translateX as % of track = -(idx / TOTAL_EXT) * 100%
  const trackStyle = {
    width: `${(TOTAL_EXT / VISIBLE) * 100}%`,
    transform: `translateX(${-(idx / TOTAL_EXT) * 100}%)`,
    transition: animated ? 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#E91E8C]/10 to-[#F5C518]/20 border border-pink-200 rounded-full px-5 py-2 mb-4">
            <span className="text-lg">⭐</span>
            <span className="text-sm font-bold text-[#E91E8C] uppercase tracking-wide">Curated by Cheyenne</span>
          </div>
          <h2
            className="text-4xl font-black text-gray-800 mb-3"
            style={{ fontFamily: 'Fredoka, Poppins, system-ui, sans-serif' }}
          >
            Cheyenne&apos;s Top Picks This Summer
          </h2>
          <p className="text-base text-gray-500 max-w-md mx-auto">
            If you don&apos;t want to scroll, start here.
          </p>
        </div>

        {/* Carousel wrapper */}
        <div className="relative">

          {/* Prev Arrow */}
          <button
            onClick={prev}
            className="absolute left-0 top-[40%] -translate-y-1/2 -translate-x-5 z-10 w-11 h-11 flex items-center justify-center bg-white rounded-full border border-pink-200 hover:border-[#E91E8C] hover:bg-pink-50 transition-all cursor-pointer"
            style={{ boxShadow: '0 2px 12px rgba(233,30,140,0.12)' }}
            aria-label="Previous"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
            </div>
          </button>

          {/* Next Arrow */}
          <button
            onClick={next}
            className="absolute right-0 top-[40%] -translate-y-1/2 translate-x-5 z-10 w-11 h-11 flex items-center justify-center bg-white rounded-full border border-pink-200 hover:border-[#E91E8C] hover:bg-pink-50 transition-all cursor-pointer"
            style={{ boxShadow: '0 2px 12px rgba(233,30,140,0.12)' }}
            aria-label="Next"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
            </div>
          </button>

          {/* Overflow mask */}
          <div
            className="overflow-hidden"
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
          >
            {/* Sliding track */}
            <div className="flex" style={trackStyle}>
              {extended.map((pick, i) => (
                <div
                  key={i}
                  className="px-3 flex-shrink-0"
                  style={{ width: `${100 / TOTAL_EXT}%` }}
                >
                  <div
                    className={`bg-white rounded-3xl overflow-hidden border hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group flex flex-col ${
                      pick.badge
                        ? 'border-[#E91E8C]/40 shadow-xl ring-2 ring-[#E91E8C]/15'
                        : 'border-pink-100 shadow-lg'
                    }`}
                    style={{
                      boxShadow: pick.badge
                        ? '0 4px 24px rgba(233, 30, 140, 0.18)'
                        : '0 4px 20px rgba(233, 30, 140, 0.08)',
                    }}
                  >
                    {/* Image */}
                    <div className="relative h-48 bg-gradient-to-br from-pink-100 to-orange-100 overflow-hidden flex-shrink-0">
                      <img
                        src={pick.image}
                        alt={pick.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#E91E8C]/30 to-transparent"></div>

                      {/* Category tag */}
                      <div className="absolute top-3 left-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap shadow-sm border ${pick.tagColor}`}>
                          {pick.tag}
                        </span>
                      </div>

                      {/* Mom's #1 Choice badge */}
                      {pick.badge && (
                        <div className="absolute top-3 right-3">
                          <span className="bg-gradient-to-r from-[#E91E8C] to-[#F5C518] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap flex items-center gap-1.5 tracking-wide">
                            <span className="text-yellow-200 text-sm">💖</span>
                            <span>{pick.badge}</span>
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5 flex flex-col flex-1">
                      <h3
                        className="text-lg font-bold text-gray-800 mb-2 leading-snug"
                        style={{ fontFamily: 'Fredoka, Poppins, system-ui, sans-serif' }}
                      >
                        {pick.name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4 leading-relaxed line-clamp-2 flex-1">
                        {pick.description}
                      </p>
                      <a
                        href={pick.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-pencil-line text-sm"></i>
                        </div>
                        {pick.buttonLabel}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center items-center gap-2 mt-7">
            {PICKS.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  i === realIdx
                    ? 'bg-[#E91E8C] w-6 h-2.5'
                    : 'bg-pink-200 hover:bg-pink-400 w-2.5 h-2.5'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
